import * as React from 'react';
import { DataSignal } from 's-js';
import * as cx from 'classnames';
import SuperCritical, { SuperCriticalComponent } from 'lib/supercritical';
import { noDefault } from 'lib/eventWrappers';

import { OrderableLookups, ItemViewModel } from './model';

import { PCSortable } from 'portcall/pcsortable/view';
import { ValidationErrorView } from 'core/validationerror/view';

type ColumnDefinition<TItem> = TextInputColumnDefinition<TItem> | SelectColumnDefinition<TItem>;

interface ColumnDisplay {
    title : string;
    className : string;
}

interface ColumnDefinitionBase<TItem> extends ColumnDisplay {
    // TH 12/12/2016 -- For now, only supporting string columns.
    // That's all this view was used for pre-typescript anyway.
    itemProp : (item : TItem) => DataSignal<string | null>;
    validationKey : string;
}

interface TextInputColumnDefinition<TItem> extends ColumnDefinitionBase<TItem> {
    type : 'textInput';
}

interface SelectColumnDefinition<TItem> extends ColumnDefinitionBase<TItem> {
    type : 'select';
    options : () => { name : () => string, value : () => string }[];
}

interface OrderableLookupsViewProps<TItem> {
    model : OrderableLookups<TItem>;
    columns : ColumnDefinition<TItem>[];
    addBtnText? : string;
    className? : string;
}

export class OrderableLookupsView<TItem> extends SuperCriticalComponent<OrderableLookupsViewProps<TItem>> {
    srender({ model: m, columns, addBtnText, className } : OrderableLookupsViewProps<TItem>) {
        return (
            <div className={cx('orderablelookups', className)}>
                <ColumnHeaders columns={columns} />
                <LookupsList model={m} columns={columns} />
                {m.SupportsAdd && <a href="" onClick={noDefault(m.Add)}>{addBtnText || '+Add'}</a>}
            </div>
        );
    }
}

let ColumnHeaders = SuperCritical(({ columns } : { columns : ColumnDisplay[] }) =>
    <div className="column-headers pc-text-label-color">
        <span className="spacer"></span>
        {columns.map(col =>
            <span className={col.className} key={col.title}>{col.title}</span>
        )}
    </div>
);

interface LookupsListProps<TItem> {
    model : OrderableLookups<TItem>;
    columns : ColumnDefinition<TItem>[];
}

class LookupsList<TItem> extends SuperCriticalComponent<LookupsListProps<TItem>> {
    srender({ model: m, columns } : LookupsListProps<TItem>) {
        return (
            <PCSortable
                className="lookup-list"
                onSort={m.OnSortItems}
                items={m.Items}
                rowFactory={item =>
                    <ListItemView
                        item={item}
                        columns={columns}
                        showDeleteBtn={m.ShowDeleteBtn}
                        key={m.ItemKey(item.Model)}
                    />
                }
            />
        );
    }
}

interface ListItemViewProps<TItem> {
    item : ItemViewModel<TItem>;
    columns : ColumnDefinition<TItem>[];
    showDeleteBtn : boolean;
}

class ListItemView<TItem> extends SuperCriticalComponent<ListItemViewProps<TItem>> {
    srender({ item, columns, showDeleteBtn } : ListItemViewProps<TItem>) {
        return (
            <li>
                <div>
                    <a href="" className={cx('delete-btn pc-close-icon', {
                            'hide-delete': !showDeleteBtn || !item.Editing()
                        })}
                        onClick={noDefault(item.Delete)}></a>
                    {columns.map(col =>
                        <ListItemColumnView item={item} column={col} key={col.title} />
                    )}
                    <a href="" className="edit-toggle" onClick={noDefault(() => item.Editing(true))}  hidden={item.Editing()}>Edit</a>
                    <a href="" className="edit-toggle" onClick={noDefault(() => item.Editing(false))} hidden={!item.Editing()}>Done</a>
                </div>
                <div>
                    {columns.map(col =>
                        <ValidationErrorView error={() => item.Validation()[col.validationKey]} key={col.title} />
                    )}
                </div>
            </li>
        );
    }
}

interface ListItemColumnViewProps<TItem> {
    item : ItemViewModel<TItem>;
    column : ColumnDefinition<TItem>;
}

class ListItemColumnView<TItem> extends SuperCriticalComponent<ListItemColumnViewProps<TItem>> {
    srender({ item, column: col } : ListItemColumnViewProps<TItem>) {
        return col.type === 'textInput' ?
            <TextInputListItemColumnView item={item} column={col} />
          : <SelectListItemColumnView    item={item} column={col} />;
    }
}

interface TextInputListItemColumnViewProps<TItem> {
    item : ItemViewModel<TItem>;
    column : TextInputColumnDefinition<TItem>;
}

class TextInputListItemColumnView<TItem> extends SuperCriticalComponent<TextInputListItemColumnViewProps<TItem>> {
    srender({ item, column: col } : TextInputListItemColumnViewProps<TItem>) {
        const prop = col.itemProp(item.Model);
        return (
            <span>
                <ItemColumnViewModeText item={item} prop={prop} className={col.className} />
                <input
                    className={cx(col.className, 'edit-mode')}
                    value={prop() || ''}
                    onChange={e => prop(e.currentTarget.value)}
                    hidden={!item.Editing()} />
            </span>
        );
    }
}

interface SelectListItemColumnViewProps<TItem> {
    item : ItemViewModel<TItem>;
    column : SelectColumnDefinition<TItem>;
} 

class SelectListItemColumnView<TItem> extends SuperCriticalComponent<SelectListItemColumnViewProps<TItem>> {
    srender({ item, column: col } : SelectListItemColumnViewProps<TItem>) {
        const prop = col.itemProp(item.Model);
        return (
            <span>
                <ItemColumnViewModeText item={item} prop={prop} className={col.className} />
                <select
                    className={cx(col.className, 'edit-mode', 'pc-select')}
                    value={prop() || ''}
                    onChange={e => prop(e.currentTarget.value)}
                    hidden={!item.Editing()}>
                    {col.options().map(o =>
                        <option key={o.value()} value={o.value()}>{o.name()}</option>
                    )}
                </select>
                <span 
                    className="pc-select-chevron"
                    hidden={!item.Editing()}>
                </span>
            </span>
        );
    }
}

let ItemColumnViewModeText = SuperCritical(({ item, prop, className } : { item : ItemViewModel<{}>, prop : () => string | null, className : string }) =>
    item.Editing() ? null : <span className={cx(className, 'pc-text-fixedwidth view-mode')}>{prop()}</span>
);


// WEBPACK FOOTER //
// ./apps/portcall/orderablelookups/view.tsx